import { safeAuthNext } from "./auth-redirect";

/**
 * Host-scoped product flavor.
 *
 * getmintai.app is the English, no-billing surface of the same app.
 * mint24.app / mint24.net keep RU+EN and Polar/YooKassa.
 *
 * Host is attacker-controlled via X-Forwarded-Host, so only exact
 * allowlisted names flip the flavor. Localhost can opt in via the
 * `site-flavor=getmintai` cookie (set by `?flavor=getmintai`) so we
 * can verify the surface without DNS.
 */

export const GETMINT_HOSTS = new Set(["getmintai.app", "www.getmintai.app"]);
export const SITE_FLAVOR_COOKIE = "site-flavor";
export const GETMINT_SITE_FLAVOR = "getmintai" as const;

export type SiteFlavor = "mint24" | "getmintai";

export function normalizeHost(host: string | null | undefined): string {
  if (!host) return "";
  return host.split(",")[0].trim().split(":")[0].toLowerCase();
}

export function isLocalHost(host: string): boolean {
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "[::1]" ||
    host.endsWith(".localhost")
  );
}

export function flavorFromHost(
  host: string | null | undefined,
  cookieFlavor?: string | null,
): SiteFlavor {
  const normalized = normalizeHost(host);
  if (GETMINT_HOSTS.has(normalized)) return "getmintai";
  if (isLocalHost(normalized) && cookieFlavor === GETMINT_SITE_FLAVOR) {
    return "getmintai";
  }
  return "mint24";
}

export function isGetmintFlavor(flavor: SiteFlavor): boolean {
  return flavor === "getmintai";
}

/** Auth availability is independent of product flavor and locale. */
export function isEmailOnlyHost(host: string | null | undefined): boolean {
  return new Set(["mint24.net", "www.mint24.net"]).has(normalizeHost(host));
}

export function internationalSignInUrl(locale: string, next?: string | null): string {
  const url = new URL(`https://mint24.app/${locale === "en" ? "en" : "ru"}/sign-in`);
  if (next) url.searchParams.set("next", safeAuthNext(next, locale));
  return url.toString();
}
